import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import { reassignPropertyOrganization } from '../api/properties-api';
import { getOrganizations } from '@/features/organizations/api/organizations-api';
import type { Organization } from '@/types/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
  currentOrganizationId: string;
  onReassigned: () => void;
}

// Sys Admin only — gating happens at the parent (button is hidden when
// the active role isn't Owner). Server-side authorization re-checks via
// User.IsInRole("Owner") at the controller, so this is a UX hint only.
export function ReassignOrganizationModal({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  currentOrganizationId,
  onReassigned,
}: Props) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [targetOrgId, setTargetOrgId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setTargetOrgId('');
      setError(null);
      return;
    }
    getOrganizations({ perPage: 100 })
      .then((res) => setOrgs(res.items.filter((o) => o.id !== currentOrganizationId)))
      .catch(() => setOrgs([]));
  }, [isOpen, currentOrganizationId]);

  async function handleConfirm() {
    if (!targetOrgId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await reassignPropertyOrganization(propertyId, targetOrgId);
      if (!result.success) {
        setError(result.message ?? 'Reassignment failed.');
        return;
      }
      onReassigned();
      onClose();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Reassignment failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reassign property to a different organization" size="md">
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-500/30 dark:bg-warning-500/10 px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-warning-600 dark:text-warning-400 flex-shrink-0" />
          <p className="text-xs text-warning-800 dark:text-warning-100 leading-relaxed">
            This moves <strong className="font-semibold">{propertyName}</strong> and all its devices,
            scheduled reports, and pairing tokens to the target organization.
            Users in the source organization will lose access on their next
            page load. Reversible only by another reassignment.
          </p>
        </div>

        <Select
          label="Target organization"
          value={targetOrgId}
          onChange={setTargetOrgId}
          placeholder="Select an organization…"
          searchable
          required
          options={orgs.map((o) => ({ value: o.id, label: o.name }))}
        />

        {error && (
          <p className="text-xs text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!targetOrgId || submitting}
            isLoading={submitting}
          >
            Move property
          </Button>
        </div>
      </div>
    </Modal>
  );
}
